import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
    FaUsers, FaPlus, FaNewspaper, FaCalendarAlt, FaTrash
} from 'react-icons/fa';
import Nav from '../components/Nav';
import postService from '../services/postService';
import groupService from '../services/groupService';
import userService from '../services/userService';

import {
    FeedComposer,
    FeedPostCard,
    FeedProfileCard,
    FeedSidebarMenu,
    FeedCategoryTabs,
    FeedSuggestedGroups,
    FeedUpcomingEvents,
    FeedPeopleYouMayKnow,
    FeedOnlineConnections
} from '../components/feed';

import FeedbackModal from '../components/shared/FeedbackModal';
import './CommunityFeed.css';

const CommunityFeedPage = () => {
    console.log("--- DEBUG: CommunityFeedPage CARGADA ---");
    const { user, token } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [groups, setGroups] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [activeCategory, setActiveCategory] = useState('todo');
    const [feedback, setFeedback] = useState({ isOpen: false, title: '', message: '', type: 'error' });
    const [upcomingEvents, setUpcomingEvents] = useState([]);

    useEffect(() => {
        loadPosts();
        loadGroups();
        loadSuggestions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadSuggestions = async () => {
        try {
            const data = await userService.getResearchers(token);
            setSuggestions(data.slice(0, 3));
        } catch (err) {
            console.error('Error loading suggestions:', err);
        }
    };

    const loadGroups = async () => {
        try {
            const data = await groupService.getGroups(token);
            
            // Filter: 1. Active groups, 2. REDPATIENT module, 3. User not a member
            const suggested = data.filter(g => {
                const isMember = g.members?.some(m => m.id === user?.profile?.id);
                const isPending = g.pendingRequests?.some(p => p.id === user?.profile?.id);
                const isPatientModule = g.creator?.user?.roles?.some(r => r.module === 'REDPATIENT');
                
                return !isMember && !isPending && isPatientModule && g.status === 'active';
            });

            setGroups(suggested.slice(0, 5).map(g => ({
                id: g.id,
                name: g.name,
                members: g.members?.length || 0,
                icon: <FaUsers style={{ color: '#FF6600' }} />
            })));
        } catch (err) {
            console.error('Error loading suggested groups:', err);
        }
    };

    const loadPosts = async () => {
        try {
            setLoading(true);
            const data = await postService.getPosts(token);
            setPosts(data);

            // Extract and process events
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const events = data
                .filter(p => p.type === 'evento' && p.metadata?.date)
                .map(ev => {
                    const d = new Date(ev.metadata.date + 'T00:00:00');
                    return { ...ev, parsedDate: d };
                })
                .filter(ev => ev.parsedDate >= today)
                .sort((a, b) => a.parsedDate - b.parsedDate)
                .map(ev => ({
                    id: ev.id,
                    day: ev.parsedDate.getDate().toString().padStart(2, '0'),
                    month: ev.parsedDate.toLocaleString('es', { month: 'short' }).toUpperCase().replace('.', ''),
                    title: ev.title,
                    meta: `${ev.metadata?.time || ''} ${ev.metadata?.location ? '- ' + ev.metadata.location : ''}`.trim()
                }));

            setUpcomingEvents(events);
        } catch (err) {
            console.error('Error loading posts:', err);
            showFeedback('Error', 'No se pudieron cargar las publicaciones.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handlePublish = async (postData) => {
        try {
            const newPost = await postService.createPost(postData, token);
            setPosts([newPost, ...posts]);
            showFeedback('Éxito', 'Publicación creada correctamente.', 'success');
        } catch (err) {
            console.error('Error publishing:', err);
            showFeedback('Error', 'No se pudo crear la publicación.', 'error');
        }
    };

    const handleDeletePost = async (postId) => {
        try {
            await postService.deletePost(postId, token);
            setPosts(posts.filter(p => p.id !== postId));
            showFeedback('Éxito', 'Publicación eliminada.', 'success');
        } catch (err) {
            showFeedback('Error', 'No se pudo eliminar la publicación.', 'error');
        }
    };

    const handleUpdatePost = (updatedPost) => {
        setPosts(posts.map(p => p.id === updatedPost.id ? updatedPost : p));
    };

    const showFeedback = (title, message, type) => {
        setFeedback({ isOpen: true, title, message, type });
    };

    const handleJoinGroup = async (groupId) => {
        try {
            await groupService.requestJoin(groupId, token);
            showFeedback('Solicitud Enviada', 'Tu solicitud ha sido enviada al administrador del grupo.', 'success');
            loadGroups(); // Refresh suggestions
        } catch (err) {
            console.error('Error joining group:', err);
            showFeedback('Error', err.response?.data?.msg || 'No se pudo enviar la solicitud.', 'error');
        }
    };

    const handleMenuAction = (action) => {
        // Reserved for future community actions
    };

    const handleConnect = async (userId) => {
        try {
            await userService.sendConnectionRequest(userId, token);
            showFeedback('Solicitud Enviada', 'Se ha enviado la invitación de conexión.', 'success');
            loadSuggestions(); // Refresh suggestions
        } catch (err) {
            console.error('Error connecting:', err);
            showFeedback('Error', 'No se pudo enviar la solicitud.', 'error');
        }
    };

    const handleMessage = (userId) => {
        navigate(`/chat?userId=${userId}`);
    };

    /* const isInvestigator = user?.roles?.some(r => 
        typeof r === 'string' ? r === 'INVESTIGATOR' : r.name === 'INVESTIGATOR'
    ); */

    // Filter posts for Community: Show only posts by non-investigator authors
    const filteredPosts = posts.filter(p => {
        // Simple author role filtering if necessary, or just category filter
        if (activeCategory === 'todo') return true;
        return p.type === activeCategory;
    });

    const communityCategories = {
        publicacion: { color: '#FF6600', label: 'Publicación' },
        evento: { color: '#22c55e', label: 'Evento' },
        discusion: { color: '#3b82f6', label: 'Discusión' }
    };

    return (
        <div className="fd-page cf-modern">
            <Nav />
            <div className="fd-layout">

                 {/* ── COLUMNA IZQUIERDA ── */}
                <aside className="fd-left">
                    <FeedProfileCard 
                        user={user} 
                        statsLeft={{ label: 'Publicaciones', value: posts.length }}
                        statsRight={{ label: 'Conexiones', value: contacts.length }}
                        onEditProfile={() => navigate('/profile-patient/edit')}
                    />
                    
                    <FeedSidebarMenu onAction={handleMenuAction} />
                    

                    <FeedSuggestedGroups 
                        groups={groups} 
                        onJoin={handleJoinGroup} 
                        token={token} 
                    />
                </aside>

                {/* ── COLUMNA CENTRAL ── */}
                <main className="fd-center">
                    <div className="fd-center-sticky">
                        <FeedComposer
                            user={user}
                            onPublish={handlePublish}
                            placeholder={t('community.feed.composerPlaceholder', 'Comparte algo con la comunidad...')}
                            showCategories={true}
                            categoryStyles={communityCategories}
                            onShowFeedback={(title, msg, type) => showFeedback(title, msg, type)}
                        />
                    </div>

                    <div className="fd-center-scroll">
                        <FeedCategoryTabs
                            activeKey={activeCategory}
                            onChange={setActiveCategory}
                            categories={[
                                { key: 'todo', label: 'Todo', icon: <FaNewspaper /> },
                                { key: 'evento', label: 'Eventos', icon: <FaCalendarAlt /> },
                                { key: 'publicacion', label: 'Publicaciones', icon: <FaNewspaper /> },
                                { key: 'discusion', label: 'Discusiones', icon: <FaUsers /> },
                            ]}
                        />
                        <div className="feed-posts-list">
                            {loading ? (
                                <div className="feed-loading">Cargando comunidad (MODO DEBUG)...</div>
                            ) : filteredPosts.length > 0 ? (
                                filteredPosts.map(post => (
                                    <FeedPostCard
                                        key={post.id}
                                        post={post}
                                        user={user}
                                        token={token}
                                        onDelete={handleDeletePost}
                                        onUpdate={handleUpdatePost}
                                        onShowFeedback={(title, msg, type) => showFeedback(title, msg, type)}
                                    />
                                ))
                            ) : (
                                <div className="feed-empty-state">
                                    <h3>La comunidad está tranquila hoy</h3>
                                    <p>Inicia una conversación compartiendo una publicación o evento.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </main>

                {/* ── COLUMNA DERECHA ── */}
                <aside className="fd-right fd-right-scroll">
                    <FeedUpcomingEvents 
                        title={t('community.feed.upcomingEvents', 'Próximos eventos')} 
                        events={upcomingEvents}
                    />
                    <FeedPeopleYouMayKnow 
                        people={suggestions} 
                        onAdd={handleConnect}
                    />
                    <FeedOnlineConnections 
                        connections={contacts} 
                        onMessage={handleMessage}
                    />
                </aside>
            </div>

            <FeedbackModal 
                isOpen={feedback.isOpen}
                title={feedback.title}
                message={feedback.message}
                type={feedback.type}
                onClose={() => setFeedback({ ...feedback, isOpen: false })}
            />
        </div>
    );
};

export default CommunityFeedPage;
